// A live, true-to-device preview of one quote slide on an iPad Pro 13" (portrait).
//
// Two jobs:
//   1. Simulate the device. The slide is laid out on a fixed 1032 x 1376 LOGICAL
//      canvas — the iPad Pro 13" portrait size in CSS points — then visually scaled
//      to fit whatever width the builder gives it (the scale is cosmetic; layout
//      math stays at true device size, so what fits here fits on the iPad).
//   2. Fit the text. As Mom adds quotes / a longer title, the body font shrinks so
//      everything fits on the one screen (no scrolling). We binary-search the largest
//      body font-size (in px @ the 1032-wide canvas) for which the content doesn't
//      overflow, then report it via onFit. The offline export reproduces that exact
//      fit with `font-size: calc(100vw * fontPx / 1032)` — no JS needed on the iPad.
//
// Styling mirrors the existing "Collection Up Close" quote screens: Georgia serif on
// black, near-white body, italic grey em-dashed attribution, a centered ~40rem column.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { Quote } from "../types";
import "./QuoteSlidePreview.css";

/** iPad Pro 13" portrait, in CSS points. The export divides by this same width. */
export const CANVAS_W = 1032;
export const CANVAS_H = 1376;
/** Safe-area padding inside the screen (px @ the canvas). Reserves room for the
 *  iOS Quick Look chrome and keeps text off the edges. Applied to the screen in CSS
 *  via inline style so the JS fit math can't drift from the layout. */
const PAD_V = 120;
const PAD_H = 80;
/** The height the content may occupy (canvas height minus the top+bottom safe area). */
const AVAIL_H = CANVAS_H - PAD_V * 2;
/** Body font-size search bounds (px @ the 1032-wide canvas). The floor is small so
 *  a very dense slide still fits (shrinks) rather than clipping. */
const MIN_FONT = 10;
const MAX_FONT = 48;

interface QuoteSlidePreviewProps {
  title: string;
  quotes: Quote[];
  /** Called with the fitted body font-size (px @ a 1032-wide canvas) after each fit. */
  onFit?: (fontPx: number) => void;
}

/** Render a quote body, turning embedded newlines into <br>. */
function textLines(text: string): React.ReactNode {
  const lines = text.split("\n");
  return lines.map((line, i) => (
    <span key={i}>
      {i > 0 && <br />}
      {line}
    </span>
  ));
}

export function QuoteSlidePreview({ title, quotes, onFit }: QuoteSlidePreviewProps) {
  // Cosmetic scale: shrink the 1032px-wide canvas to fit the builder column.
  const [scale, setScale] = useState(0.5);
  const frameRef = useRef<HTMLDivElement>(null);
  // The padded screen box (its clientHeight is the available height) and the
  // measured content column (its offsetHeight is the content height — both are
  // layout px, unaffected by the cosmetic transform).
  const bodyRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Keep onFit in a ref so re-fitting doesn't depend on the caller memoizing it.
  const onFitRef = useRef(onFit);
  onFitRef.current = onFit;

  const trimmedTitle = title.trim();
  const hasContent = trimmedTitle !== "" || quotes.length > 0;
  // A cheap signature so the fit effect re-runs only on real content changes.
  const contentSig = JSON.stringify({ t: trimmedTitle, q: quotes });

  // --- Fit the font to the screen (binary search; runs before paint) ---------
  useLayoutEffect(() => {
    const body = bodyRef.current;
    const content = contentRef.current;
    if (!body || !content) return;

    let lo = MIN_FONT;
    let hi = MAX_FONT;
    let best = MIN_FONT;
    // ~8 iterations converges sub-pixel across the [13, 48] range. We compare the
    // content column's layout height against the fixed safe-area height (AVAIL_H),
    // both in true canvas px (the cosmetic transform doesn't affect offsetHeight).
    for (let i = 0; i < 8; i++) {
      const mid = (lo + hi) / 2;
      body.style.fontSize = `${mid}px`;
      if (content.offsetHeight <= AVAIL_H) {
        best = mid;
        lo = mid;
      } else {
        hi = mid;
      }
    }
    const fitted = Math.floor(best);
    body.style.fontSize = `${fitted}px`;
    onFitRef.current?.(fitted);
  }, [contentSig]);

  // --- Cosmetic scale-to-fit -------------------------------------------------
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const measure = () => {
      const avail = frame.clientWidth;
      if (avail > 0) setScale(Math.min(1, avail / CANVAS_W));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(frame);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="qsp-frame" ref={frameRef}>
      <div
        className="qsp-scaled"
        style={{ width: CANVAS_W * scale, height: CANVAS_H * scale }}
      >
        <div
          className="qsp-canvas"
          style={{ transform: `scale(${scale})` }}
          role="img"
          aria-label="Quote slide preview on an iPad Pro 13 inch"
        >
          <div
            className="qsp-body"
            ref={bodyRef}
            style={{ padding: `${PAD_V}px ${PAD_H}px` }}
          >
            <div className="qsp-content" ref={contentRef}>
              {trimmedTitle && <h2 className="qsp-title">{trimmedTitle}</h2>}
              {quotes.map((q, i) => {
                const attr = q.attribution?.trim();
                return (
                  <blockquote className="qsp-quote" key={i}>
                    <p className="qsp-text">{textLines(q.text)}</p>
                    {attr && <p className="qsp-attr">&mdash; {attr}</p>}
                  </blockquote>
                );
              })}
              {!hasContent && (
                <p className="qsp-placeholder">
                  Your quote slide will preview here, sized to fit an iPad Pro 13″.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
